'use client';

import React, { useState, useEffect, useRef, useContext, Suspense, useReducer, useCallback, useMemo } from 'react';
import deepEqual from 'deep-equal';
import { ArrowCircleLeftIcon, ArrowCircleRightIcon, MagicWandIcon, XCircleIcon, EraserIcon, XIcon, BookmarksIcon } from '@phosphor-icons/react';
import { DatePicker } from "@heroui/date-picker";
import { TimeInput } from "@heroui/date-input";
import { Input } from "@heroui/input";
import { Alert } from "@heroui/alert";
import { Button } from "@heroui/button";
import { Tooltip } from '@heroui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@heroui/popover';
import { Time } from "@internationalized/date";
import { CalendarDate } from "@internationalized/date";
import { useDateFormatter } from "@react-aria/i18n";
import { Slide, SourceObject, MediaObject, EvidenceObject, Source, LLMConfig } from '@/shared/types';
import Visibility from './cells/visibility';
import { dedupeSources, dedupeMediaSources, getDescriptionFromAnswerText } from '@/shared/clientFunctions';
import { EventFromChat } from './answer-item';
import { extractPathAfterBulk, getClientTimeFromStr, getDateFromDateStr, getTimeFromStr, getMediaType, getTimezoneList, FetchWithVersionCheck, getBestMediaFromSources, CaptureError, getTokenInfo, getUserEmailForHeaders, getUsername, getUiSourceFromChatSource } from '@/shared/functions';
import { Card, CardBody, CardFooter, CardHeader } from "@heroui/card";
import Select from "react-select";
import { TzSelectStyles } from '@/shared/styleDefs';
import UnifiedSources from './cells/unifiedSources';
import { HotKeys } from '@/components/hot-keys';
import { Checkbox } from '@heroui/checkbox';
import { FullScreenContext, FeaturesContext, ToastContext, AuthContext } from '@/app/contexts';
import MediaDisplay from './ui/mediaDisplay';
import { REFUSAL_MESSAGES, PASS_MESSAGES, LLM_CONFIG, UsableMediaTypes, MODEL_ARNS_LIST, ENRICHMENT_POLL_INTERVAL_MS, ENRICHMENT_POLL_TIMEOUT_MS, isEnrichmentHistoryPending } from '@/shared/constants';
import dynamic from 'next/dynamic';
import { ToastLevel } from './ui/toast';
import { useToolsDrawer } from './tools-drawer';
const MarkdownEditor = dynamic(() => import('./ui/markdownEditor'), {
  ssr: false
});
type OptionType = {
  value: string | null;
  label: string;
};
type FormData = {
  id?: string | null | undefined;
  displayOrder?: number;
  tags?: string[];
  slideTitle?: string;
  slideSubTitle?: string;
  slideDescription?: string;
  media?: MediaObject[];
  show?: boolean;
  date?: string | null;
  time?: string | null;
  isTimeValid?: boolean;
  eventLocalTimezone?: string | null;
  sources?: SourceObject[];
  displayDate?: string | null;
  eventType?: string | null;
  isDirty?: boolean;
};
type EnrichmentApiResponse = {
  output?: string;
  citations?: Source[];
};
type EnrichmentResult = {
  slideDescription: string;
  sources: SourceObject[];
  media: MediaObject[];
};

/**
 * Computes enrichment from API response (description, sources, media).
 * 
 * @param result - The API response to compute enrichment from
 * @returns The enrichment result or null if there is no output in the response
 */
export const computeEnrichmentFromApiResponse = (result: EnrichmentApiResponse): EnrichmentResult | null => {
  if (!result?.output) return null;
  const answerText = ((result.output || "") as string).replace(/<sup>.*?<\/sup>/g, "").replace(/<\/p><p>/g, "").trim();
  const citations = result.citations ?? [];
  const description = getDescriptionFromAnswerText(answerText);
  const sources = citations.map(citation => getUiSourceFromChatSource(citation));
  const media = getBestMediaFromSources(sources);
  return {
    slideDescription: description,
    sources: Array.from(new Set(sources)) as SourceObject[],
    media
  };
};

/**
 * Form snapshot shape used when applying enrichment.
 * All fields needed to build the full form after enrichment.
 */
type EventFormSnapshot = {
  id?: string | null;
  displayOrder?: number;
  slideTitle?: string;
  slideSubTitle?: string | undefined;
  slideDescription?: string;
  date?: string | null;
  displayDate?: string | null;
  time?: string | null;
  isTimeValid?: boolean;
  eventLocalTimezone?: string | null;
  eventType?: string | null;
  sources?: SourceObject[];
  media?: MediaObject[];
  show?: boolean;
};

/**
* Merges enrichment result into current form snapshot.
 * 
 * @param currentForm - The current form snapshot to merge enrichment into
 * @param enrichment - The enrichment result to merge into the current form snapshot, 
 * using currentForm.slideDescription as fallback when enrichment.slideDescription is empty
 * @returns The new form snapshot with the enrichment merged in
 */
const applyEnrichmentToForm = (currentForm: EventFormSnapshot, enrichment: EnrichmentResult): EventFormSnapshot => {
  return {
    ...currentForm,
    slideDescription: enrichment.slideDescription?.trim() !== "" ? enrichment.slideDescription : currentForm.slideDescription ?? "",
    sources: (currentForm.sources ?? []) as SourceObject[],
    media: (currentForm.media ?? []) as MediaObject[]
  };
};
const normalizeFromSourceDescription = (description?: string): string => {
  if (!description) return '';
  // So if we a chat chunk happens to begin with break tags like this:
  // 
  // "<br><br><br>status: Read<br>label: Default<br>platform: Unknown<br>"
  //  
  // It can cause the WYSIWYG editor to break and render nothing. This seems to be
  // a known issue with the editor. 
  // 
  // Thus, we normalize common HTML breaks to markdown/plain newlines to 
  // make sure we always have something to render in the description box
  // even if the chat chunk begins with a lot of break tags.
  return description.replace(/<br\s*\/?>/gi, '\n').trim();
};
const getCurrentEventFromChat = (eventsFromChat: EventFromChat[], index: number): Slide | null => {
  if (!eventsFromChat || !eventsFromChat?.[index]) return null;
  const chatEventToAdd = eventsFromChat?.[index];
  if (chatEventToAdd) {
    const description = chatEventToAdd.answerText === 'FROM_SOURCE' ? normalizeFromSourceDescription(chatEventToAdd.slideDescription) : getDescriptionFromAnswerText(chatEventToAdd.answerText);
    const sourcesFromChatEvent = chatEventToAdd.sources ? chatEventToAdd.sources.map((source: Source) => {
      const sourceToUse: SourceObject = {
        sourceType: extractPathAfterBulk(source.sourceFilePath || '').sourceType || 'Unknown',
        sourceUrl: source.sourceFilePath
      };
      if (source.sourceContext) {
        sourceToUse.sourceContext = source.sourceContext;
      }
      if (source.sourceContext?.pageNumber || source.pageNumber) {
        sourceToUse.pageNumber = source.sourceContext?.pageNumber || source.pageNumber;
      }
      return sourceToUse;
    }) : [] as SourceObject[];
    const sourcesForEvents = dedupeSources(sourcesFromChatEvent);
    const newEvent = {
      id: 'NEW_EVENT_FROM_CHAT_0',
      slideTitle: chatEventToAdd.answerText === 'FROM_SOURCE' ? 'FROM SOURCE' : '',
      slideDescription: description,
      show: true,
      approval: 'approved',
      sources: sourcesForEvents,
      media: chatEventToAdd.media as MediaObject[] || []
    };
    return newEvent;
  }
  return null;
};
const timezonesWithoffsets = getTimezoneList() || [];

/**
 * Gets the initial form data for the event modal.
 * 
 * @param params - The parameters to get the initial form data from
 * @returns The initial form data or null if there is no initial form data
 */
function getInitialFormData(params: {
  localColumn: string | null | undefined;
  eventsFromChat: EventFromChat[] | undefined;
  events: Slide[] | undefined;
  currentEventIndex: number | null;
  mediaSourcesFromChat: MediaObject[] | undefined;
  sourcesFromChat: SourceObject[] | undefined;
  enriching: boolean;
  currentFormRefValue: FormData | null;
  preEnrichEventValue: FormData | null;
}): FormData | null {
  const {
    localColumn,
    eventsFromChat,
    events,
    currentEventIndex,
    mediaSourcesFromChat,
    sourcesFromChat,
    enriching,
    currentFormRefValue,
    preEnrichEventValue
  } = params;
  if (localColumn === "newEventFromChat" && eventsFromChat && !enriching) {
    const currentEventFromChat = getCurrentEventFromChat(eventsFromChat, 0);
    if (!currentEventFromChat) return null;
    let mediaItemFromChat = currentEventFromChat.media || [];
    if (mediaItemFromChat.length === 0) {
      if (currentEventFromChat.slideTitle === "FROM SOURCE") {
        mediaItemFromChat = getBestMediaFromSources(currentEventFromChat.sources as SourceObject[]) || [];
        currentEventFromChat.slideTitle = "";
      } else {
        mediaItemFromChat = getBestMediaFromSources(mediaSourcesFromChat || []) || [];
      }
      currentEventFromChat.media = mediaItemFromChat as MediaObject[];
    }
    let combinedSourcesFromChat = (currentEventFromChat.sources || []) as SourceObject[];
    if (currentEventFromChat.media) {
      const sourcesFromMedia = currentEventFromChat.media.map((mediaItem: MediaObject) => ({
        sourceType: mediaItem.mediaType,
        sourceUrl: mediaItem.mediaUrl,
        sourceContext: mediaItem.mediaContext
      })) as SourceObject[];
      combinedSourcesFromChat = combinedSourcesFromChat.concat(sourcesFromMedia);
    }
    if (combinedSourcesFromChat?.length === 1) {
      if (currentEventFromChat.media?.length === 0) {
        currentEventFromChat.media = getBestMediaFromSources(currentEventFromChat.sources as SourceObject[]);
      }
      currentEventFromChat.slideTitle = "";
    }
    currentEventFromChat.sources = dedupeSources(combinedSourcesFromChat).filter((sourceTest: SourceObject) => sourceTest.sourceUrl != null && sourceTest.sourceUrl !== undefined && sourceTest.sourceUrl.trim() !== "");
    return {
      ...currentEventFromChat,
      isTimeValid: true
    } as FormData;
  }
  if (localColumn === "duplicate" && !enriching && currentEventIndex !== null) {
    const duplicateEvent = {
      ...events?.[currentEventIndex],
      show: true
    } as Slide & {
      id?: string;
    };
    delete duplicateEvent.id;
    return duplicateEvent as FormData;
  }
  if (localColumn === "create" && !enriching) {
    const mediaItemFromChat = mediaSourcesFromChat?.[0];
    const useable = mediaItemFromChat && UsableMediaTypes.includes(getMediaType(mediaItemFromChat, mediaItemFromChat.sourceInfo));
    const media = useable ? [mediaItemFromChat] : [];
    return {
      id: "NEW_EVENT_FROM_TABLE",
      slideTitle: "",
      slideSubTitle: undefined,
      slideDescription: "",
      show: true,
      sources: dedupeSources(sourcesFromChat || []) || [],
      media: media || [],
      isTimeValid: true
    };
  }
  if (currentEventIndex !== null && (events && events?.[currentEventIndex] || enriching) && !currentFormRefValue) {
    const currentEvent = preEnrichEventValue ?? events?.[currentEventIndex];
    let descriptionForForm = currentEvent?.slideDescription || "";
    if (descriptionForForm.startsWith("```")) {
      descriptionForForm = descriptionForForm.replace("```", "");
      if (descriptionForForm.startsWith("json ")) {
        descriptionForForm = descriptionForForm.replace("json ", "");
      }
    }
    return {
      id: currentEvent?.id || null,
      displayOrder: currentEvent?.displayOrder || -1,
      date: currentEvent?.date ? currentEvent.date : null,
      displayDate: currentEvent?.displayDate ? currentEvent.displayDate : currentEvent?.date || null,
      time: currentEvent?.time ? currentEvent.time : null,
      isTimeValid: currentEvent?.isTimeValid === true ? true : false,
      eventLocalTimezone: currentEvent?.eventLocalTimezone || null,
      slideTitle: currentEvent?.slideTitle || "",
      slideSubTitle: currentEvent?.slideSubTitle || undefined,
      slideDescription: descriptionForForm,
      eventType: currentEvent?.eventType || "",
      sources: (currentEvent?.sources || []) as SourceObject[],
      media: (currentEvent?.media || []) as MediaObject[],
      show: currentEvent?.show ?? false
    };
  }
  if (!currentFormRefValue && !enriching) {
    return {
      id: "NEW",
      slideTitle: "",
      slideDescription: "",
      slideSubTitle: undefined,
      show: true,
      isTimeValid: true
    };
  }
  if (localColumn === "unifiedSourcesChanges" && currentFormRefValue) {
    return {
      ...currentFormRefValue
    };
  }
  return null;
}
const NEW_EVENT_COLUMNS = ['create', 'duplicate', 'newEventFromChat'];
interface EventModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddEvent: (newEvent: Slide) => Promise<void>;
  onEditEvent: (editedEvent: Slide) => Promise<void>;
  column?: string | null;
  events?: Slide[];
  eventIndex: number | null;
  isLegacy: boolean;
  editingEnabled: boolean;
  sourcesFromChat?: SourceObject[];
  mediaSourcesFromChat?: MediaObject[];
  eventsFromChat?: EventFromChat[];
  availableEvidence?: EvidenceObject[];
  caseId?: string;
}
const stripDirty = (x: FormData | null | undefined): Omit<FormData, 'isDirty'> | null | undefined => {
  if (x === undefined) return undefined;
  if (x === null) return null;
  const {
    isDirty: _omit,
    ...rest
  } = x;
  return rest;
};
const hasUnsavedChanges = (formData: FormData | undefined, newFormData: FormData | null) => {
  return !deepEqual(stripDirty(formData), stripDirty(newFormData));
};

// NOTE -- Jake Mason | (2026-02-03)
// Maybe overkill, but I prefer to use enums whenever possible even if it's just for a small set like these
enum FormReducerActionType {
  SetForm = "SET_FORM",
  SetField = "SET_FIELD",
}
type FormReducerAction = {
  type: FormReducerActionType.SetForm;
  payload: FormData | undefined;
} | {
  type: FormReducerActionType.SetField;
  payload: Partial<FormData>;
};
function formReducer(state: FormData | undefined, action: FormReducerAction): FormData | undefined {
  switch (action.type) {
    case FormReducerActionType.SetForm:
      return action.payload;
    case FormReducerActionType.SetField:
      return {
        ...(state ?? {}),
        ...action.payload
      } as FormData;
    default:
      return state;
  }
}

// NOTE -- Jake Mason | (2026-02-04):
// This is a memoized component that isolates the sources/media state so changes don't re-render the whole modal.
// It is memoized so the parent re-renders (title, date, etc.) don't re-render this or UnifiedSources.
const EventModalSourcesSection = React.memo(function EventModalSourcesSection({
  eventKey,
  initialSources,
  initialMedia,
  onSourcesChange,
  ...unifiedSourcesProps
}: {
  eventKey: number | string;
  initialSources?: SourceObject[];
  initialMedia?: MediaObject[];
  onSourcesChange: (sources: SourceObject[], media: MediaObject[]) => void;
} & Omit<React.ComponentProps<typeof UnifiedSources>, 'value' | 'mediaValue' | 'handleUnifiedSourcesChanges'>) {
  const [sources, setSources] = useState<SourceObject[]>(initialSources ?? []);
  const [media, setMedia] = useState<MediaObject[]>(initialMedia ?? []);
  useEffect(() => {
    setSources(initialSources ?? []);
    setMedia(initialMedia ?? []);
  }, [eventKey, initialSources, initialMedia]);
  const handleChange = useCallback((newSources: SourceObject[], newMedia: MediaObject[]) => {
    setSources(newSources);
    setMedia(newMedia);
    onSourcesChange(newSources, newMedia);
  }, [onSourcesChange]);
  return <UnifiedSources {...unifiedSourcesProps} value={sources} mediaValue={media} handleUnifiedSourcesChanges={handleChange} />;
});
const EventModal: React.FC<EventModalProps> = ({
  isOpen,
  onClose,
  onAddEvent,
  onEditEvent,
  column,
  events,
  eventIndex,
  isLegacy,
  editingEnabled,
  sourcesFromChat,
  mediaSourcesFromChat,
  eventsFromChat,
  caseId
}) => {
  // NOTE -- Jake Mason | (2026-02-03):
  // using a reducer here makes updating the code pure and should be much easier to test
  // instead of the previous form that was a set of state mutations and multiple setters/getters
  const [formData, dispatchForm] = useReducer(formReducer, undefined);
  const [showAlertDialog, setShowAlertDialog] = useState(false);
  const [currentEventIndex, setCurrentEventIndex] = useState<number | null>(eventIndex);
  const [navDir, setNavDir] = useState<'left' | 'right' | 'no'>('no');
  const [markdownValue, setMarkdownValue] = useState('');
  const [enrichWithScope, setEnrichWithScope] = useState(localStorage.getItem('enrichWithScope') === 'true');
  const [enrichPopoverVisible, setEnrichPopoverVisible] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [enrichedData, setEnrichedData] = useState<Slide | null>(null);
  const [enrichObject, setEnrichObject] = useState({
    enrichTitle: true,
    enrichDescription: true,
    enrichSources: true,
    enrichMedia: true
  });
  const [undoEnrichEvent, setUndoEnrichEvent] = useState(false);
  const [canUndoEnrichEvent, setCanUndoEnrichEvent] = useState(false);
  const [refusalMessages, setRefusalMessages] = useState<string[]>(REFUSAL_MESSAGES);
  const [passMessages, setPassMessages] = useState<string[]>(PASS_MESSAGES);
  const [modelArnsList, setModelArnsList] = useState<any>(null);
  const [llmConfig, setLlmConfig] = useState<LLMConfig>(LLM_CONFIG);
  const [localColumn, setLocalColumn] = useState(column);
  const {
    idToken,
    accessToken
  } = useContext(AuthContext);
  const {
    featureFlags
  } = useContext(FeaturesContext) || {};
  const {
    setToast,
    setToastOpen
  } = useContext(ToastContext);
  const {
    isFullscreen
  } = useContext(FullScreenContext) || {
    isFullscreen: false
  };
  const toolsDrawer = useToolsDrawer();
  const registerApplyTextToEventDescription = toolsDrawer?.registerApplyTextToEventDescription;
  const formDataRef = useRef<FormData | null>(null);
  const formDataInitial = useRef<FormData | null>(null);
  const eventsRef = useRef<Slide[]>([]);
  const timeInputRef = useRef<HTMLInputElement>(null);
  const headerInputRef = useRef<HTMLInputElement>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const currentEventIndexRef = useRef<number | null>(null);
  const preEnrichEvent = useRef<FormData | null>(null);
  const filteredMediaSourcesFromChat = useMemo(() => mediaSourcesFromChat?.filter((mediaItem: MediaObject) => UsableMediaTypes.includes(getMediaType(mediaItem, mediaItem.sourceInfo))) ?? [], [mediaSourcesFromChat]);
  const newEvent = formData && formData.id === 'NEW' || formData && formData.id && formData.id.split('_')[0] === 'NEW' || localColumn === 'create' || localColumn === 'duplicate' || localColumn === 'newEventFromChat';

  // load new data if this is opened with a new eventIndex
  useEffect(() => {
    if (eventIndex !== null) {
      setCurrentEventIndex(eventIndex);
    }
  }, [eventIndex]);
  useEffect(() => {
    if (currentEventIndex === null) return;
    if (currentEventIndexRef.current !== currentEventIndex) {
      formDataRef.current = null;
      preEnrichEvent.current = null;
      currentEventIndexRef.current = currentEventIndex;
    }
  }, [events, currentEventIndex]);
  const onTimezoneSelect = (option: OptionType) => {
    dispatchForm({
      type: FormReducerActionType.SetField,
      payload: {
        eventLocalTimezone: option.value,
        isDirty: true
      }
    });
  };
  const handleEnrichWithScopeChange = (isSelected: boolean) => {
    setEnrichWithScope(isSelected);
    localStorage.setItem('enrichWithScope', isSelected.toString());
  };

  // Keep ref in sync for async handlers (e.g. enrichEvent) that need latest form
  useEffect(() => {
    formDataRef.current = formData ?? null;
  }, [formData]);
  const handleDateChange = (date: CalendarDate | null) => {
    if (date) {
      const dateStr = `${date.year}-${date.month}-${date.day}`;
      dispatchForm({
        type: FormReducerActionType.SetField,
        payload: {
          date: dateStr,
          isDirty: true
        }
      });
    } else {
      dispatchForm({
        type: FormReducerActionType.SetField,
        payload: {
          date: null,
          isDirty: true
        }
      });
    }
  };
  const onTimeIsValidChange = (isSelected: boolean) => {
    dispatchForm({
      type: FormReducerActionType.SetField,
      payload: {
        isTimeValid: !isSelected,
        isDirty: true
      }
    });
  };
  useDateFormatter({
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
  const handleUndoEnrichEvent = () => {
    formDataRef.current = null;
    const formDataToSet = preEnrichEvent.current ?? undefined;
    if (formDataToSet) {
      dispatchForm({
        type: FormReducerActionType.SetForm,
        payload: {
          ...formDataToSet,
          isDirty: true
        }
      });
    }
    setEnriching(false);
    setMarkdownValue(formDataToSet?.slideDescription || '');
    if (enrichedData) {
      setEnrichedData(null);
    }
    setCanUndoEnrichEvent(false);
    setUndoEnrichEvent(false);
  };
  useEffect(() => {
    const modelArnsListFromFeatureFlag = featureFlags?.['modelArnsList'];
    if (modelArnsListFromFeatureFlag) {
      setModelArnsList(modelArnsListFromFeatureFlag);
    } else if (featureFlags?.['systemStage']) {
      const systemStage = featureFlags?.['systemStage'] as string;
      const modelArnsListHardcoded = MODEL_ARNS_LIST(systemStage);
      setModelArnsList(modelArnsListHardcoded);
    } else {
      const modelArnsListHardcoded = MODEL_ARNS_LIST('prod');
      setModelArnsList(modelArnsListHardcoded);
    }

    // Refusal Messages from the llmConfig feature flag take precedence, so check both
    // this code is currently duplicated in answer-item.tsx
    const refusalMessagesFromFeatureFlag: any = featureFlags?.['refusalMessages'];
    const passMessagesFromFeatureFlag: any = featureFlags?.['passMessages'];
    const llmConfigFromFeatureFlag = featureFlags?.['llmConfig'];
    const llmConfigRefusalMessages = (llmConfigFromFeatureFlag as unknown as LLMConfig)?.refusalMessages;
    const llmConfigPassMessages = (llmConfigFromFeatureFlag as unknown as LLMConfig)?.passMessages;
    const refusalMessagesArray = refusalMessagesFromFeatureFlag?.refusalMessages && Array.isArray(refusalMessagesFromFeatureFlag.refusalMessages) ? refusalMessagesFromFeatureFlag.refusalMessages : [];
    const passMessagesArray = passMessagesFromFeatureFlag?.passMessages && Array.isArray(passMessagesFromFeatureFlag.passMessages) ? passMessagesFromFeatureFlag.passMessages : [];
    const llmConfigRefusalMessagesArray = Array.isArray(llmConfigRefusalMessages) ? llmConfigRefusalMessages : [];
    const llmConfigPassMessagesArray = Array.isArray(llmConfigPassMessages) ? llmConfigPassMessages : [];
    if (llmConfigRefusalMessagesArray.length) {
      setRefusalMessages(llmConfigRefusalMessagesArray);
    } else if (refusalMessagesArray.length) {
      setRefusalMessages(refusalMessagesArray);
    } else {
      setRefusalMessages(REFUSAL_MESSAGES);
    }
    if (llmConfigPassMessagesArray.length) {
      setPassMessages(llmConfigPassMessagesArray);
    } else if (passMessagesArray.length) {
      setPassMessages(passMessagesArray);
    } else {
      setPassMessages(PASS_MESSAGES);
    }

    // LLM Config from the llmConfig feature flag takes precedence and overrides the refusalMessages flag too
    if (llmConfigFromFeatureFlag) {
      const lDllmConfig: LLMConfig = llmConfigFromFeatureFlag as unknown as LLMConfig;
      if (lDllmConfig.refusalMessages !== undefined && lDllmConfig.passMessages !== undefined && lDllmConfig.topP !== undefined && lDllmConfig.textResponsePartDelimiter !== undefined && lDllmConfig.overheadPerPair !== undefined && lDllmConfig.markdownDirective !== undefined && lDllmConfig.promptTemplate !== undefined && lDllmConfig.conversationContext !== undefined && lDllmConfig.promptTemplateWithContext !== undefined && lDllmConfig.orchestrationPromptTemplate !== undefined && lDllmConfig.historyWordLimit !== undefined && lDllmConfig.historyCharLimit !== undefined && lDllmConfig.temperature !== undefined && lDllmConfig.unalignedTemperature !== undefined && lDllmConfig.maxTokens !== undefined && lDllmConfig.numOfResults !== undefined && lDllmConfig.overrideSearchType !== undefined && lDllmConfig.supportedQueryTransformationConfigTypes !== undefined) {
        setLlmConfig(lDllmConfig as unknown as LLMConfig);
      }
    }
  }, [featureFlags]);
  const enrichEvent = async (useScope: boolean) => {
    if (currentEventIndex === null) {
      return;
    }
    setEnriching(true);
    preEnrichEvent.current = formData || null;
    setToast({
      setOpen: setToastOpen,
      open: true,
      location: 'bottom-right',
      time: Date.now(),
      duration: 60000,
      title: 'Attempting to enrich this event...',
      description: 'This may take up to 1 minute...',
      level: ToastLevel.INFO
    });
    let query = '';
    if (featureFlags?.enrichEventPrompt && (featureFlags?.enrichEventPrompt as string).trim() !== '') {
      const title = formDataRef.current?.slideTitle || formData?.slideTitle || undefined;
      const description = markdownValue || formDataRef.current?.slideDescription || formData?.slideDescription || '';
      query = featureFlags?.enrichEventPrompt as string;
      if (title && title.trim() !== '') {
        query = query.replace(/<titlePrompt>([\S\s]*?)<\/titlePrompt>/g, '$1');
        query = query.replace('{title}', title);
      } else {
        query = query.replace(/<titlePrompt>([\S\s]*?)<\/titlePrompt>/g, '');
      }
      if (description && description.trim() !== '') {
        query = query.replace(/<descriptionPrompt>([\S\s]*?)<\/descriptionPrompt>/g, '$1');
        query = query.replace('{description}', description);
      } else {
        query = query.replace(/<descriptionPrompt>([\S\s]*?)<\/descriptionPrompt>/g, '');
      }
    } else {
      query = 'Tell me more about';
      let useAnd = false;
      const titleForQuery = formDataRef.current?.slideTitle ?? formData?.slideTitle ?? '';
      if (enrichObject.enrichTitle && titleForQuery.trim() !== '') {
        query += ` "${titleForQuery}" as it relates to this case`;
        useAnd = true;
      }
      if (enrichObject.enrichDescription && markdownValue && markdownValue.trim() !== '') {
        query += `${useAnd ? ' and' : ''} "${markdownValue}" as it relates to this case`;
        useAnd = true;
      }
    }
    try {
      const chatPath = '/api/protected/chat-service-proxy';
      const idToken = document.cookie.split('; ').find(row => row.startsWith("idToken="))?.split('=')[1];
      const accessTokenCookie = document.cookie.split('; ').find(row => row.startsWith("accessToken="))?.split('=')[1];
      const idTokenInfo = getTokenInfo(idToken || '');
      const accessTokenInfo = getTokenInfo(accessTokenCookie || '');
      const userId = getUserEmailForHeaders(idTokenInfo, accessTokenInfo);
      let body: any = {
        enrichEvent: true,
        query: query,
        selectedCase: caseId,
        caseId: caseId,
        username: getUsername(idTokenInfo),
        files: useScope ? (formDataRef.current?.sources ?? formData?.sources)?.map((source: SourceObject) => source.sourceUrl) : [],
        scopes: [],
        excludedFiles: [],
        excludedScopes: [],
        userId: userId,
        sessionId: null,
        retryCount: 0,
        refusalMessages: refusalMessages,
        passMessages: passMessages,
        modelArnsList: modelArnsList,
        llmConfig: llmConfig,
        useQueryDecomposition: false,
        useOpenSearch: !!featureFlags?.['useOpenSearchForEnrich'] || false,
        showPreliminaryResult: false,
        useNewTimeSaved: false
      };
      const pollHeaders = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken || accessTokenCookie}`,
        'x-tai-id-token': idToken || ''
      };
      let rawResult = await FetchWithVersionCheck(chatPath, {
        method: 'POST',
        headers: pollHeaders,
        body: JSON.stringify(body)
      });
      let result = await rawResult.json();
      body = {
        enrichEvent: result?.enrich_event ?? false,
        sessionId: result?.sessionId,
        epochTimeSecs: result?.epoch_time_secs,
        chatKey: result?.chat_key,
        requestType: result?.request_type
      };
      const startTime = Date.now();
      while (isEnrichmentHistoryPending(result?.status) && Date.now() - startTime < ENRICHMENT_POLL_TIMEOUT_MS) {
        await new Promise(resolve => setTimeout(resolve, ENRICHMENT_POLL_INTERVAL_MS));
        rawResult = await FetchWithVersionCheck('/api/protected/chat/history', {
          method: 'POST',
          headers: pollHeaders,
          body: JSON.stringify(body)
        });
        result = await rawResult.json();
      }
      if (result?.status === 'error') {
        return formData as Slide;
      }
      const answer = JSON.parse(result?.answer);
      const enrichment = computeEnrichmentFromApiResponse(answer);
      if (enrichment) {
        const enrichedSources = enrichment.sources ?? [];
        const enrichedMedia = enrichment.media ?? [];
        const currentSources = formDataRef.current?.sources ?? events?.[currentEventIndex]?.sources ?? [];
        const currentMedia = formDataRef.current?.media ?? events?.[currentEventIndex]?.media ?? [];
        const newSources = dedupeSources([...currentSources, ...enrichedSources]);
        const newMedia = dedupeMediaSources([...currentMedia, ...enrichedMedia]);
        const current = formDataRef.current ?? events?.[currentEventIndex];
        const currentSnapshot: EventFormSnapshot = {
          id: current?.id ?? events?.[currentEventIndex]?.id ?? null,
          displayOrder: current?.displayOrder ?? events?.[currentEventIndex]?.displayOrder ?? -1,
          date: current?.date ?? events?.[currentEventIndex]?.date ?? null,
          displayDate: current?.displayDate ?? events?.[currentEventIndex]?.displayDate ?? events?.[currentEventIndex]?.date ?? null,
          time: current?.time ?? events?.[currentEventIndex]?.time ?? null,
          isTimeValid: current?.isTimeValid ?? events?.[currentEventIndex]?.isTimeValid === true,
          eventLocalTimezone: current?.eventLocalTimezone ?? events?.[currentEventIndex]?.eventLocalTimezone ?? null,
          slideTitle: current?.slideTitle || events?.[currentEventIndex]?.slideTitle || '',
          slideSubTitle: current?.slideSubTitle ?? events?.[currentEventIndex]?.slideSubTitle ?? undefined,
          slideDescription: markdownValue || current?.slideDescription || events?.[currentEventIndex]?.slideDescription || '',
          eventType: current?.eventType ?? events?.[currentEventIndex]?.eventType ?? '',
          sources: newSources,
          media: newMedia,
          show: current?.show ?? events?.[currentEventIndex]?.show ?? false
        };
        const newFormData = applyEnrichmentToForm(currentSnapshot, enrichment) as FormData;
        setCanUndoEnrichEvent(true);
        setEnrichedData(newFormData as Slide);
        setMarkdownValue(newFormData.slideDescription || '');
        dispatchForm({
          type: FormReducerActionType.SetForm,
          payload: {
            ...newFormData,
            isDirty: true
          }
        });
        setTimeout(() => {
          setToast({
            setOpen: setToastOpen,
            open: true,
            location: 'bottom-right',
            time: Date.now(),
            duration: 3000,
            title: 'This event has been enriched',
            description: 'You may notice some changes to the description and sources. You can edit these as needed.',
            level: ToastLevel.SUCCESS
          });
        }, 500);
      } else {
        throw new Error('Could not compute enrichment from API response');
      }
    } catch (error) {
      setTimeout(() => {
        CaptureError('Error enriching event: ' + error, error as Error, !!featureFlags?.useIntercom);
        setToast({
          setOpen: setToastOpen,
          open: true,
          location: 'bottom-right',
          time: Date.now(),
          duration: 3000,
          title: 'This event could not be enriched',
          description: 'Change the title, description, sources, media, and/or uncheck the "Use Selected Sources as Scope" and try again.',
          level: ToastLevel.ERROR
        });
      }, 500);
    }
  };
  useEffect(() => {
    setLocalColumn(column);
  }, [column]);
  useEffect(() => {
    if (!newEvent && (currentEventIndexRef.current !== currentEventIndex || undoEnrichEvent || currentEventIndex === null)) {
      return;
    }
    if (events && !deepEqual(events, eventsRef.current)) {
      eventsRef.current = events;
      formDataRef.current = null;
    }
    const initial = getInitialFormData({
      localColumn,
      eventsFromChat,
      events,
      currentEventIndex,
      mediaSourcesFromChat,
      sourcesFromChat,
      enriching,
      currentFormRefValue: formDataRef.current,
      preEnrichEventValue: preEnrichEvent.current
    });
    if (initial !== null && !formData?.isDirty) {
      if (deepEqual(initial, formDataRef.current)) return;
      formDataRef.current = initial;
      formDataInitial.current = initial;
      dispatchForm({
        type: FormReducerActionType.SetForm,
        payload: initial
      });
      setMarkdownValue(initial.slideDescription || '');
      if (enrichedData) setEnrichedData(null);
      setEnrichObject({
        enrichTitle: !!initial.slideTitle?.trim(),
        enrichDescription: !!initial.slideDescription?.trim(),
        enrichSources: !!initial.sources?.length,
        enrichMedia: !!initial.media?.length
      });
    } else if (enrichedData) {
      dispatchForm({
        type: FormReducerActionType.SetForm,
        payload: {
          ...(enrichedData as FormData),
          isDirty: true
        }
      });
      setEnrichedData(null);
    }
  }, [events, currentEventIndex, localColumn, eventsFromChat, enrichedData, undoEnrichEvent, enriching, mediaSourcesFromChat, sourcesFromChat, newEvent]);
  const scrollLeft = () => {
    if (currentEventIndex === null) {
      return;
    }
    const formDataToCheck = {
      ...formData,
      ...(formDataRef.current ?? {}),
      slideDescription: markdownValue,
      slideTitle: formData?.slideTitle,
      slideSubTitle: formData?.slideSubTitle
    };
    if (hasUnsavedChanges(formDataToCheck, formDataInitial.current)) {
      setNavDir('left');
      setShowAlertDialog(true);
    } else {
      formDataRef.current = null;
      formDataInitial.current = null;
      dispatchForm({
        type: FormReducerActionType.SetForm,
        payload: undefined
      });
      setCurrentEventIndex(currentEventIndex - 1);
    }
  };
  const scrollRight = () => {
    if (currentEventIndex === null) {
      return;
    }
    const formDataToCheck = {
      ...formData,
      ...(formDataRef.current ?? {}),
      slideDescription: markdownValue,
      slideTitle: formData?.slideTitle,
      slideSubTitle: formData?.slideSubTitle
    };
    if (hasUnsavedChanges(formDataToCheck, formDataInitial.current)) {
      setNavDir('right');
      setShowAlertDialog(true);
    } else {
      formDataRef.current = null;
      formDataInitial.current = null;
      dispatchForm({
        type: FormReducerActionType.SetForm,
        payload: undefined
      });
      setCurrentEventIndex(currentEventIndex + 1);
    }
  };
  const handleMarkdownChange = (newMarkdown: string) => {
    if (!editingEnabled || isLegacy) return;
    if (!newMarkdown || newMarkdown.trim() === '') {
      setEnrichObject({
        ...enrichObject,
        enrichDescription: false
      });
    } else {
      setEnrichObject({
        ...enrichObject,
        enrichDescription: true
      });
    }
    // Only update if the content has actually changed
    setMarkdownValue(newMarkdown);
  };
  const handleMarkdownChangeFinal = () => {
    if (!editingEnabled || isLegacy) return;
    if (!markdownValue || markdownValue.trim() === '') {
      setEnrichObject({
        ...enrichObject,
        enrichDescription: false
      });
    } else {
      setEnrichObject({
        ...enrichObject,
        enrichDescription: true
      });
    }
    dispatchForm({
      type: FormReducerActionType.SetField,
      payload: {
        slideDescription: markdownValue,
        isDirty: true
      }
    });
  };
  const applyTextToEventDescriptionImplRef = useRef<(text: string) => void>(() => {});
  applyTextToEventDescriptionImplRef.current = (text: string) => {
    if (!editingEnabled || isLegacy) return;
    const t = text ?? "";
    if (!t.trim()) {
      setEnrichObject(prev => ({
        ...prev,
        enrichDescription: false
      }));
    } else {
      setEnrichObject(prev => ({
        ...prev,
        enrichDescription: true
      }));
    }
    setMarkdownValue(t);
    dispatchForm({
      type: FormReducerActionType.SetField,
      payload: {
        slideDescription: t,
        isDirty: true
      }
    });
  };
  useEffect(() => {
    if (!registerApplyTextToEventDescription) return;
    if (!isOpen || !editingEnabled || isLegacy) {
      registerApplyTextToEventDescription(null);
      return;
    }
    registerApplyTextToEventDescription(txt => applyTextToEventDescriptionImplRef.current(txt));
    return () => registerApplyTextToEventDescription(null);
  }, [isOpen, editingEnabled, isLegacy, registerApplyTextToEventDescription]);
  const handleTimePresses = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!editingEnabled || isLegacy) return;
    const hourMinuteOrSecond = (e.target as any)?.dataset?.type;
    if (hourMinuteOrSecond !== 'second') {
      let timeStr = (timeInputRef.current?.innerText || '').replace(/\p{Pd}/gu, '_');
      timeStr = timeStr.replace(/[^0-9:AMP_]*/g, '').replace('PM', ' PM').replace('AM', ' AM');
      const {
        hoursNumber,
        minutesNumber,
        secondsNumber
      } = getTimeFromStr(timeStr);
      if (!isNaN(hoursNumber as any) && !isNaN(minutesNumber as any) && isNaN(secondsNumber as any)) {
        const newTime = new Time(hoursNumber, minutesNumber, 0);
        handleTimeChange(newTime);
      }
    }
  };
  const handleTimeChange = (time: Time | null) => {
    if (!editingEnabled || isLegacy) return;
    const date = formData?.date ? getDateFromDateStr(formData.date) : null;
    dispatchForm({
      type: FormReducerActionType.SetField,
      payload: {
        date: date ? date.toString() : null,
        time: time ? `${time.hour}:${time.minute}:${time.second}` : null,
        isDirty: true
      }
    });
  };
  const handleVisibilityClick = async () => {
    if (!events) return;
    dispatchForm({
      type: FormReducerActionType.SetField,
      payload: {
        show: !formData?.show,
        isDirty: true
      }
    });
  };
  const handleSubmit = (e: React.FormEvent) => {
    if (!editingEnabled || isLegacy || currentEventIndex === null && !newEvent) {
      return;
    }
    if (e.preventDefault) {
      e.preventDefault();
    }
    const formDataToSet = {
      ...formData,
      sources: formDataRef.current?.sources ?? formData?.sources,
      media: formDataRef.current?.media ?? formData?.media,
      slideTitle: formData?.slideTitle,
      slideSubTitle: formData?.slideSubTitle,
      slideDescription: markdownValue
    };
    if (formDataToSet && !formDataToSet?.time) {
      formDataToSet.isTimeValid = false;
      formDataToSet.time = undefined;
      formDataToSet.eventLocalTimezone = undefined;
      formDataToSet.slideDescription = markdownValue;
    }
    // Strip UI-only fields before persisting (isDirty must not be sent to API)
    const {
      isDirty: _omit,
      ...eventPayload
    } = formDataToSet;
    if (newEvent) {
      onAddEvent(eventPayload as unknown as Slide);
      setShowAlertDialog(false);
    } else {
      onEditEvent(eventPayload as unknown as Slide);
      setShowAlertDialog(false);
    }
    if (navDir !== 'right' && navDir !== 'left') {
      setShowAlertDialog(false);
      setCurrentEventIndex(0);
      onClose();
    } else {
      const currentIndex = currentEventIndex === null ? 0 : currentEventIndex;
      const newEventIndex = navDir === 'right' ? currentIndex + 1 : currentIndex - 1;
      setCurrentEventIndex(newEventIndex);
    }
    setNavDir('no');
  };
  const handleUnifiedSourcesChanges = useCallback((sources: SourceObject[], media: MediaObject[]) => {
    if (!editingEnabled || isLegacy) return;
    formDataRef.current = {
      ...(formDataRef.current ?? {}),
      sources,
      media
    } as FormData;
    dispatchForm({
      type: FormReducerActionType.SetField,
      payload: {
        sources,
        media,
        isDirty: true
      }
    });
    setEnrichObject(prev => ({
      ...prev,
      enrichSources: sources.length > 0,
      enrichMedia: media.length > 0
    }));
  }, [editingEnabled, isLegacy]);
  const handleCancel = () => {
    const formDataToCheck = {
      ...formData,
      ...(formDataRef.current ?? {}),
      slideDescription: markdownValue,
      slideTitle: formData?.slideTitle,
      slideSubTitle: formData?.slideSubTitle
    };
    const unsavedChanges = hasUnsavedChanges(formDataToCheck, formDataInitial.current);
    if (unsavedChanges) {
      setNavDir('no');
      setShowAlertDialog(true);
    } else {
      dispatchForm({
        type: FormReducerActionType.SetForm,
        payload: undefined
      });
      onClose();
    }
  };
  const handleAlertDialogClose = () => {
    setShowAlertDialog(false);
    if (currentEventIndex === null) {
      return;
    }
    if (formDataRef.current) {
      dispatchForm({
        type: FormReducerActionType.SetForm,
        payload: formDataRef.current
      });
    }
    if (navDir === 'right') {
      dispatchForm({
        type: FormReducerActionType.SetForm,
        payload: undefined
      });
      setCurrentEventIndex(currentEventIndex + 1);
    } else if (navDir === 'left') {
      dispatchForm({
        type: FormReducerActionType.SetForm,
        payload: undefined
      });
      setCurrentEventIndex(currentEventIndex - 1);
    } else {
      dispatchForm({
        type: FormReducerActionType.SetForm,
        payload: formDataRef.current ?? undefined
      });
      onClose();
    }
    formDataRef.current = null;
    formDataInitial.current = null;
  };
  const handleAlertDialogConfirm = () => {
    setShowAlertDialog(false);
    if (currentEventIndex === null) {
      return;
    }
    handleSubmit(new Event('submit') as any);
    formDataRef.current = null;
    formDataInitial.current = null;
    dispatchForm({
      type: FormReducerActionType.SetForm,
      payload: undefined
    });
    if (navDir === 'right') {
      setCurrentEventIndex(currentEventIndex + 1);
    } else if (navDir === 'left') {
      setCurrentEventIndex(currentEventIndex - 1);
    } else {
      onClose();
    }
  };
  return <>
      {!undoEnrichEvent && (!!events && currentEventIndex !== null && !!events?.[currentEventIndex] || !!newEvent) ? <Card className='eventModalCard w-[90vw] sm:min-w-[90vw] sm:min-h-[calc(100vh-120px)]' role="dialog" aria-modal="true" aria-labelledby={`event-modal-title-${(currentEventIndex || 0) + 1}`} aria-describedby={`event-modal-description-${(currentEventIndex || 0) + 1}`} classNames={{
      base: 'box-border flex flex-col absolute top-[60px] sm:top-[85px] left-[20px] sm:left-1/2 sm:transform sm:-translate-x-1/2 sm:mx-4 bg-gray-600 items-center rounded-lg',
      header: 'flex sm:items-center sm:justify-center rounded-t-lg border-b-[1px] border-white/20 border-solid bg-gray-700 m-0 p-1 h-fit',
      body: 'box-border flex items-center overflow-auto w-[90vw] sm:w-[calc(100vw-40px)] h-[calc(100vh-275px)] custom-scrollbar2 px-2 sm:px-0',
      footer: 'flex items-center justify-center rounded-b-lg border-t-[1px] border-white/20 border-solid bg-gray-700 m-0 p-1 h-[70px]'
    }}>
          <HotKeys onCloseModal={handleCancel} isModal={true} />
          <CardHeader id={`event-modal-title-${(currentEventIndex || 0) + 1}`}>
            <div className="flex flex-row items-center justify-start w-full">
              <div className={`flex flex-row items-start sm:items-center gap-4 w-[90%] sm:p-2 sm:pl-4`}>
                {!NEW_EVENT_COLUMNS.includes(column || '') && <>
                    <button aria-label="Previous Event" type="button" className={`hidden sm:block hover:opacity-100 ${(currentEventIndex || 0) < 1 ? 'opacity-20 hover:opacity-20' : 'cursor-pointer opacity-70'}`} disabled={(currentEventIndex || 0) < 1} onClick={scrollLeft}>
                      <ArrowCircleLeftIcon weight="duotone" size={32} />
                    </button>
                    <button aria-label="Next Event" type="button" className={`hidden sm:block hover:opacity-100 ${(currentEventIndex || 0) > (events?.length || 0) - 2 ? 'opacity-20 hover:opacity-20' : 'cursor-pointer opacity-70'}`} disabled={(currentEventIndex || 0) > (events?.length || 0) - 2} onClick={scrollRight}>
                      <ArrowCircleRightIcon weight="duotone" size={32} />
                    </button>
                  </>}
                <div className="flex flex-row items-center justify-start p-2 sm:p-0 sm:pl-6">
                  <div className="flex flex-row flex-wrap justify-start items-center text-lg font-light tracking-wide m-0 leading-none">
                    {editingEnabled && !isLegacy ? <div className="flex flex-row sm:justify-start items-start sm:items-center">
                        <div className="flex flex-col sm:justify-items-start text-white font-light leading-none">
                          <div className='flex flex-row sm:items-center sm:justify-start text-sm text-white/70 uppercase'>{!!newEvent ? 'Add New Event' : 'Event '}{currentEventIndex !== -1 ? `${(currentEventIndex || 0) + 1}` : ''}</div>
                          <div className='flex flex-row sm:items-center sm:justify-start text-lg sm:text-xl leading-tight'>{formData?.slideTitle || 'Title Not Specified'}</div>
                        </div>
                      </div> : <div className="flex flex-row justify-start items-center">
                        <div className="text-white font-light tracking-wider leading-none text-lg sm:text-2xl m-0 p-0 sm:pb-[12px]">Details for Event {events?.[currentEventIndex || 0]?.displayOrder} {events?.length ? `of ${events.length}` : ''}</div>
                      </div>}
                  </div>
                </div>
              </div>
              {(toolsDrawer || localColumn !== 'timelineTitle') && <div className="flex flex-row gap-2 justify-end items-center pr-2 sm:pr-5 shrink-0">
                {toolsDrawer && <Tooltip content="Favorites" placement={'bottom'} shouldCloseOnBlur={true} showArrow={true} delay={0} offset={5} classNames={{
              content: 'bg-gray-800 text-white rounded-md border-0 border-transparent drop-shadow-none inner-shadow-none shadow-none px-1.5 py-1 w-[100px] text-center',
              base: 'before:bg-gray-800 before:rounded-[2px] before:shadow-none before:drop-shadow-none border-0 before:w-[25px] before:h-[25px]'
            }}>
                    <button type="button" aria-label="Open tools drawer" title="Media & Sources tools" className="hover:opacity-100 hover:bg-white/10 border-1 border-white/10 hover:border-white/40 border-solid rounded-md p-1.5
                        cursor-pointer opacity-70 flex items-center justify-center" onClick={() => toolsDrawer.openDrawerToTab('sources')}>
                      <BookmarksIcon weight="duotone" size={26} />
                    </button>
                  </Tooltip>}
                {localColumn !== 'timelineTitle' && <>
                  {canUndoEnrichEvent && <button aria-label="Undo" type="button" className="hover:opacity-100 hover:bg-white/10 border-1 border-white/10 hover:border-white/40 border-solid rounded-md px-2 py-1
                        cursor-pointer disabled:cursor-not-allowed opacity-70 disabled:opacity-60
                        sm:flex flex-row gap-2 justify-center items-center hidden" disabled={!canUndoEnrichEvent} onClick={handleUndoEnrichEvent}>
                      <EraserIcon weight="duotone" size={26} />
                      <label className={`text-white/100 font-light tracking-wider text-[14px] m-0 p-0 cursor-pointer text-nowrap`}>Undo</label>
                    </button>}
                  <Popover showArrow={true} placement="bottom-end" classNames={{
                base: 'before:bg-gray-800 before:rounded-[2px] before:shadow-none before:drop-shadow-none border-0 before:w-[25px] before:h-[25px]'
              }} isOpen={enrichPopoverVisible} onOpenChange={setEnrichPopoverVisible}>
                    <PopoverTrigger className="mt-1">
                      <button type="button" aria-label="Enrich Event" aria-haspopup="dialog" className="hover:opacity-100 hover:bg-white/10 border-1 border-white/10 hover:border-white/40 border-solid rounded-md px-2 py-1
                          cursor-pointer disabled:cursor-not-allowed opacity-70 disabled:opacity-60
                          sm:flex flex-row gap-2 justify-center items-center hidden">
                        <MagicWandIcon weight="duotone" size={26} />
                        <span className={`text-white/100 font-light tracking-wider text-nowrap text-[14px] m-0 p-0 cursor-pointer ${(!formData?.slideTitle || formData?.slideTitle?.trim() === '') && (!formData?.slideDescription || formData?.slideDescription?.trim() === '') ? 'text-white/30' : ''}`}>Enrich Event</span>
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="h-fit bg-gray-800">
                      <div className="flex flex-col gap-2 mt-2 mb-2 w-[300px]">
                        <div className="text-white/100 font-light text-[14px] text-wrap tracking-wider m-0 p-0">Enriching an event affects description, sources, and media. The title is not changed.</div>
                        <div className="flex flex-row gap-2 items-center">
                          <Checkbox type="checkbox" aria-label="Enrich with scope" isSelected={enrichWithScope} onValueChange={handleEnrichWithScopeChange} />
                          <label className="text-white/100 font-light tracking-wider text-[14px] m-0 p-0">Use Selected Sources as Scope</label>
                        </div>
                        <div className="flex flex-row gap-2 items-center">
                          <button aria-label="Enrich Event" type="button" className="w-1/2 hover:opacity-100 hover:bg-white/10 border-1 border-white/30 hover:border-white/40 border-solid rounded-md px-2 py-1
                              cursor-pointer disabled:cursor-not-allowed opacity-80 disabled:opacity-60 text-white/65 sm:flex flex-row gap-2 justify-center items-center hidden" disabled={Object.values(enrichObject).every(value => value === false)} onClick={() => {
                        enrichEvent(enrichWithScope);
                        setEnrichPopoverVisible(false);
                      }}>
                            <MagicWandIcon weight="duotone" size={20} />
                            <div className={`text-white/100 font-light tracking-wider text-[14px] m-0 p-0`}> Enrich Event</div>
                          </button>
                          <button aria-label="Cancel Enrich Event" type="button" className="w-1/2 hover:opacity-100 hover:bg-white/10 border-1 border-white/30 hover:border-white/40 border-solid rounded-md px-2 py-1
                              cursor-pointer disabled:cursor-not-allowed opacity-80 disabled:opacity-60 text-white/65 sm:flex flex-row gap-2 justify-center items-center hidden" disabled={Object.values(enrichObject).every(value => value === false)} onClick={() => {
                        setEnrichPopoverVisible(false);
                      }}>
                            <XIcon size={20} />
                            <div className={`text-white/100 font-light tracking-wider text-[14px] m-0 p-0`}>Cancel</div>
                          </button>
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                  </>}
              </div>}
            </div>
          </CardHeader>
          <CardBody>
            <div onSubmit={handleSubmit} id={`event-modal-description-${(currentEventIndex || 0) + 1}`} className="flex flex-col gap-8 items-center text-white font-inter bg-transparent sm:min-w-[300px] w-full sm:w-[calc(70vw)] sm:max-w-fit">
              <div className="flex flex-col gap-12 items-start w-full sm:w-[800px]">
                <div className="flex flex-col gap-2 sm:gap-8 items-start w-full">
                  <div className="flex flex-row gap-2 items-start w-full">
                    <div className="flex flex-col gap-2 w-full">
                      <div className="flex flex-row gap-2 items-center">
                        <label className="flex flex-row gap-2 items-center text-white font-light tracking-wider text-[14px]">Header
                        </label>
                      </div>
                      <Input ref={headerInputRef} aria-label="Header" disabled={!editingEnabled && !isLegacy} name="slideSubTitle" variant="flat" label="" radius="sm" labelPlacement="outside" value={formData?.slideSubTitle ?? ''} onValueChange={value => dispatchForm({
                    type: FormReducerActionType.SetField,
                    payload: {
                      slideSubTitle: value,
                      isDirty: true
                    }
                  })} size="md" placeholder="Optional: This is shown above the event title in place of the date and time." classNames={{
                    base: 'box-border w-full mr-[-17px]',
                    inputWrapper: 'box-border'
                  }} />
                    </div>
                    <div className="flex flex-row justify-start items-center gap-2 pt-[32px] whitespace-nowrap">
                      <Visibility data={formData || {}} size={24} onClick={handleVisibilityClick} /> {formData?.show ? "Event Shown" : "Event Not Shown"}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 items-start">
                    <div className="flex flex-col sm:flex-row gap-2 items-start">
                      <div className="flex flex-row gap-2 items-start">
                        <DatePicker aria-label="Date" value={formData?.date ? getDateFromDateStr(formData.date) : null} onChange={handleDateChange} label={"Date"} labelPlacement="outside" size="md" radius="sm" classNames={{
                      base: 'w-fit',
                      selectorButton: 'hover:text-gray-800 rounded-lg hover:bg-black/40 cursor-pointer',
                      popoverContent: 'text-gray-800 rounded-md shadow-lg',
                      calendar: 'rounded-md text-black border-2 border-solid border-white/10',
                      calendarContent: 'text-gray-800'
                    }} />
                        <button type="button" aria-label="Clear Date" onClick={() => handleDateChange(null)} className="ml-[20px] mt-[21px] cursor-pointer flex items-start justify-start hover:text-red-500 rounded-full">
                          <XCircleIcon weight="duotone" size={24} />
                        </button>
                      </div>
                      {formData?.date && <>
                          <div className="flex flex-row items-center justify-center gap-1">
                            <TimeInput ref={timeInputRef} isDisabled={!formData?.date} value={formData?.time ? getClientTimeFromStr(formData.time) : null} onChange={handleTimeChange} radius="sm" classNames={{
                        base: 'text-white w-fit'
                      }} variant="flat" labelPlacement="outside" label="Time" size="md" granularity="second" onKeyUp={handleTimePresses} />
                            <button type="button" aria-label="Clear Time" onClick={() => handleTimeChange(null)} className="ml-[20px] cursor-pointer flex items-center justify-center p-1 hover:text-red-500 rounded-full">
                              <XCircleIcon weight="duotone" size={24} />
                            </button>
                          </div>
                          <div className="box-border flex flex-col justify-start items-center gap-1">
                            <div className="text-white/100 w-full font-light text-left text-[14px] mt-[2px] p-0 whitespace-nowrap">Timezone</div>
                            <div className="box-border flex flex-row justify-start items-center rounded-md mt-[2px]">
                              <Select<OptionType, false> styles={TzSelectStyles as any} className="zIndexSelect-51" classNamePrefix='react-select' aria-label='Select Timezone' placeholder='Select Timezone' value={formData?.eventLocalTimezone ? {
                          value: formData.eventLocalTimezone,
                          label: formData.eventLocalTimezone
                        } : null} options={timezonesWithoffsets?.map((tzObj, index) => ({
                          value: tzObj.name,
                          label: `${tzObj.name} (${tzObj.offset})`
                        }))} onChange={option => option && onTimezoneSelect(option)} components={{
                          Option: ({
                            data,
                            innerProps
                          }: {
                            data: OptionType;
                            innerProps: any;
                          }) => <div {...innerProps} className="box-border p-0 flex flex-row justify-left bg-[#0F1D32] rounded-sm">
                                      <div className="box-border w-full rounded-sm py-[5px] px-[3px]
                                      bg-slate-800 text-[14px] cursor-pointer dir-rtl whitespace-nowrap
                                      truncate border-1 border-white border-solid opacity-100% z-1000">
                                        &lrm;{data.label}
                                      </div>
                                    </div>
                        }} />
                              <button type="button" aria-label="Clear Timezone" onClick={() => onTimezoneSelect({
                          value: null,
                          label: ''
                        })} className="ml-[5px] mt-[-12px] mb-[15px] cursor-pointer flex items-start justify-start hover:text-red-500 rounded-full">
                                <XCircleIcon weight="duotone" size={24} />
                              </button>
                            </div>
                          </div>
                        </>}
                    </div>
                    {formData?.date && <div className="flex flex-row items-center justify-start gap-1 sm:pl-[200px]">
                        <Tooltip content={`Time ${formData?.isTimeValid ? 'is currently' : 'will not be'} displayed for this event.`} placement={'bottom'} shouldCloseOnBlur={true} delay={0} offset={0} classNames={{
                    content: 'bg-gray-900 text-white rounded-md border-0 border-transparent drop-shadow-none inner-shadow-none shadow-none px-1.5 py-1 w-[200px] text-center'
                  }}>
                          <Checkbox type="checkbox" aria-label="Is Time Valid" isSelected={!formData?.isTimeValid} onValueChange={onTimeIsValidChange} />
                        </Tooltip>
                        <label className="text-white/100 font-light tracking-wider text-[14px] m-0 p-0 sm:whitespace-nowrap">Use event time only for chronological ordering.</label>
                      </div>}
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-2 items-start w-full sm:w-[800px]">
                <label htmlFor="event-title-input" className="text-white/100 font-light tracking-wider text-[14px] m-0 p-0">Title</label>
                <Input ref={titleInputRef} id="event-title-input" disabled={!editingEnabled && !isLegacy} name="slideTitle" variant="flat" label="" labelPlacement="outside" value={formData?.slideTitle === 'FROM SOURCE' ? '' : formData?.slideTitle ?? ''} onValueChange={value => dispatchForm({
              type: FormReducerActionType.SetField,
              payload: {
                slideTitle: value,
                isDirty: true
              }
            })} size="md" radius="sm" classNames={{
              base: 'box-border max-w-[770px] mr-[-17px] w-full',
              inputWrapper: 'box-border'
            }} />
              </div>
              <div className="flex flex-col gap-2 items-start w-full sm:w-[800px]">
                <label htmlFor="event-description-editor" className="text-white/100 font-light tracking-wider text-[14px] m-0 p-0">Description</label>
                <Suspense fallback={<div>Loading...</div>}>
                  <MarkdownEditor id="event-description-editor" markdown={markdownValue} onChange={handleMarkdownChange} onBlurIn={handleMarkdownChangeFinal} debounceMs={300} />
                </Suspense>
              </div>
              <div className="flex flex-col w-full sm:w-[800px]">
                <EventModalSourcesSection eventKey={currentEventIndex ?? 'new'} initialSources={formData?.sources as SourceObject[]} initialMedia={formData?.media as MediaObject[]} onSourcesChange={handleUnifiedSourcesChanges} caseId={caseId || ''} isInModal={true} editingEnabled={editingEnabled && !isLegacy} sourcesFromChat={sourcesFromChat} mediaSourcesFromChat={filteredMediaSourcesFromChat} isTitleSlide={false} />
              </div>
            </div>
          </CardBody>
          <CardFooter>
            {showAlertDialog && <Alert hideIcon={true} classNames={{
          base: 'box-border bg-black text-black/40 absolute bottom-[5px] flex items-start justify-start border-white/20 border-solid border-[1px] shadow-lg w-fit h-fit',
          mainWrapper: 'box-border flex flex-col items-start justify-start m-0 p-0',
          title: 'text-blue-400 font-bold text-lg',
          description: 'text-white italic'
        }}>
                <div className="flex flex-row items-center gap-2">
                  <span className="text-white/100 font-light tracking-wider text-[14px] m-0 p-0 whitespace-nowrap">Unsaved Changes:</span>
                  <Button title="Close" onPress={() => handleAlertDialogClose()} className="bg-white/30 text-black/70 cursor-pointer">
                    Close
                  </Button>
                  <Button title="Save and Close" onPress={() => handleAlertDialogConfirm()} className="bg-white/60 text-white/90 cursor-pointer border-white/80 border-solid border-2">
                    Save and {!navDir || navDir === 'no' ? 'Close' : navDir === 'right' ? 'go to Next Slide' : 'go to Previous Slide'}
                  </Button>
                </div>
              </Alert>}
            {(!editingEnabled || isLegacy) && <div className="flex flex-row justify-center items-center w-full gap-4 my-1">
                <button className="btn-text secondary bg-white/70 hover:bg-white/90 text-gray-700 hover:text-black" aria-label="Cancel" onClick={handleCancel}>
                  Close
                </button>
              </div>}
            {editingEnabled && !isLegacy && <div className="flex flex-row justify-center items-center w-full gap-4 my-1">
                <button className="btn-text secondary bg-white/70 hover:bg-white/90 text-gray-700 hover:text-black" aria-label="Cancel" onClick={handleCancel}>
                  Cancel
                </button>
                <button aria-label="Save Event" type="submit" className="btn-text bg-green-500 hover:bg-green-600 text-white opacity-100 border-2 border-solid border-white/10 font-bold" onClick={handleSubmit}>
                  {!!newEvent ? "Add Event" : "Save Changes"}
                </button>
              </div>}
          </CardFooter>
        </Card> : <>
          {!isFullscreen && events && events?.length > 0 && (formDataRef.current?.media ?? formData?.media)?.length ? <MediaDisplay mediaItem={(formDataRef.current?.media ?? formData?.media)?.[0]!} isTitleSlide={false} /> : null}
        </>}
    </>;
};
export default EventModal;